/**
 * Structural equality, for deciding whether a draft still matches what is
 * stored.
 *
 * Written for the event editor's dirty flag. That flag used to mean "`onChange`
 * fired at least once", which is not the same question: a control that patches
 * the draft with the value it already held, or a tab that writes an object back
 * with a new identity, set it permanently and there was no way back short of
 * saving. What anyone actually wants to know is whether the record differs.
 *
 * Deliberately plain. These documents come from JSON, so there are no Dates,
 * Maps, Sets or cycles to handle, and pulling in a library to compare two plain
 * objects would be the larger change.
 *
 * ARRAYS COMPARE IN ORDER, which is right here: the order of `SelectedGroups`
 * and `Notices` is the order they were chosen in and the order they are stored,
 * so a reordering is a change.
 *
 * A key present with `undefined` is NOT the same as a key that is absent. That
 * is the honest reading for this data: the API omits what an event does not
 * have, so a key appearing at all is the draft having touched it.
 */
export function deepEqual(a: unknown, b: unknown): boolean {
	if (a === b) return true;

	// NaN is never `===` itself. The API sanitises NaN out of event documents,
	// so this should not arise; it is here so that if one ever does, it does not
	// read as an edit on every keystroke.
	if (typeof a === "number" && typeof b === "number") {
		return Number.isNaN(a) && Number.isNaN(b);
	}

	if (typeof a !== "object" || typeof b !== "object") return false;
	if (a === null || b === null) return false;

	if (Array.isArray(a) || Array.isArray(b)) {
		if (!Array.isArray(a) || !Array.isArray(b)) return false;
		if (a.length !== b.length) return false;
		return a.every((item, i) => deepEqual(item, b[i]));
	}

	const left = a as Record<string, unknown>;
	const right = b as Record<string, unknown>;
	const keys = Object.keys(left);
	if (keys.length !== Object.keys(right).length) return false;
	return keys.every(
		(key) =>
			Object.prototype.hasOwnProperty.call(right, key) &&
			deepEqual(left[key], right[key]),
	);
}

/**
 * The dotted paths at which two documents differ. Names only, never values.
 *
 * The diagnostic half of `deepEqual`, for the question "it says there are
 * unsaved changes and I have not touched anything". Knowing THAT a draft
 * differs is not actionable; knowing it differs at `Approval.RSVP.ReminderDays`
 * names the culprit immediately.
 *
 * VALUES ARE NEVER RETURNED, and that is a rule rather than an omission: the
 * event document carries attendee PII, and this exists to be logged. A path is
 * a field name, which is safe to print; what is in it is not.
 *
 * Arrays are reported as a single path rather than per index. An array that has
 * been reordered or added to differs as a whole, and a list of forty indices
 * would bury the one line that matters.
 */
export function changedPaths(
	draft: unknown,
	saved: unknown,
	prefix = "",
): string[] {
	if (deepEqual(draft, saved)) return [];

	const plain = (v: unknown) =>
		typeof v === "object" && v !== null && !Array.isArray(v);
	if (!plain(draft) || !plain(saved)) return [prefix || "(root)"];

	const left = draft as Record<string, unknown>;
	const right = saved as Record<string, unknown>;
	const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
	const out: string[] = [];
	for (const key of keys) {
		const path = prefix ? `${prefix}.${key}` : key;
		const inLeft = Object.prototype.hasOwnProperty.call(left, key);
		const inRight = Object.prototype.hasOwnProperty.call(right, key);
		if (!inLeft) out.push(`${path} (absent in draft)`);
		else if (!inRight) out.push(`${path} (added by the draft)`);
		else out.push(...changedPaths(left[key], right[key], path));
	}
	return out;
}
